import dayjs from 'dayjs';
import _ from 'lodash';
import JSEncrypt from 'jsencrypt';
import history from 'src/libs/history';
import qs from 'query-string';

import {
  CATEGORIES_ID,
  DATE_FORMAT,
  MACHINING_METHOD,
  REGEX,
  ROUND,
  SIMPLE_DATE_FORMAT,
  MACHINING_PROCESSING_RANGES,
} from 'src/constants/app';
import LocalStorage from './LocalStorage';
import { t } from 'src/libs/i18n';
import { ProductType } from 'src/enums/quotation';

type IsImportedProductOnlyParam = {
  product: string;
  checkZ0: boolean;
  method?: string;
};

type ConvertFinishedSizeType = {
  size: string;
  unitSize: string;
  manufacturingSizes;
  product: string;
  method: string;
  inputValue;
};

export const isValidPassword = (pwd) => {
  const containsNumber = REGEX.REGEX_NUMBER;
  const containsUpperCase = REGEX.REGEX_UPPER_CASE;
  const containsLowerCase = REGEX.REGEX_LOWER_CASE;
  const containsSymbol = REGEX.REGEX_SPECIAL_CHARACTER;

  let matches = 0;

  if (containsNumber.test(pwd)) {
    matches++;
  }

  if (containsUpperCase.test(pwd)) {
    matches++;
  }

  if (containsLowerCase.test(pwd)) {
    matches++;
  }

  if (containsSymbol.test(pwd)) {
    matches++;
  }

  return pwd && matches >= 2;
};

export const mbStrWidth = (input) => {
  let len = 0;
  for (let i = 0; i < input?.length; i++) {
    let code = input.charCodeAt(i);
    if (
      (code >= 0x0020 && code <= 0x1fff) ||
      (code >= 0xff61 && code <= 0xff9f)
    ) {
      len += 1;
    } else if ((code >= 0x2000 && code <= 0xff60) || code >= 0xffa0) {
      len += 2;
    } else {
      len += 0;
    }
  }
  return len;
};

export const allowNaNNumber = (currentValue, originalValue) => {
  return originalValue === '' ? null : currentValue;
};

export const formatCountOnUnit = (
  countValue = 0,
  countSuffix = '枚',
  unitValue = 0,
  unitSuffix = 'kg',
  henkanJury = '',
) => {
  const format =
    henkanJury && henkanJury !== ''
      ? `${countValue}${countSuffix} ${henkanJury} (${unitValue}${unitSuffix})`
      : `${countValue}${countSuffix}(${unitValue}${unitSuffix})`;
  return format;
};

export const formatPrice = (value, currency) => {
  return `${value}${currency}`;
};

export const checkExpired = (value: string) => {
  const date = dayjs(value, DATE_FORMAT);
  if (date.isBefore(dayjs().format(DATE_FORMAT))) {
    return true;
  }
  return false;
};

export const optionSelect = (size: 200 | 500 = 200) => {
  const PAGE_SIZE_OPTIONS_200 = [
    { label: '10', value: 10 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
  ];
  const PAGE_SIZE_OPTIONS_500 = [
    { label: '10', value: 10 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
    { label: '300', value: 300 },
    { label: '400', value: 400 },
    { label: '500', value: 500 },
  ];
  if (size === 200) {
    return PAGE_SIZE_OPTIONS_200;
  }
  return PAGE_SIZE_OPTIONS_500;
};

export const validateFormat = (date, format) => {
  return dayjs(date, format).format(format) === date;
};

export const openPDF = (pdf) => {
  window.open(pdf);
};

export const subtractDays = (date, days) => {
  return dayjs(date).subtract(days, 'day').format(SIMPLE_DATE_FORMAT);
};

export const checkOrdered = (item) => {
  if (
    item.kessaiZumiFlg === '1' ||
    (item.chumonUketkeNo !== null && item.chumonUketkeNo !== undefined)
  ) {
    return true;
  }
  return false;
};

export const getTypeQuotation = (item) => {
  if (checkOrdered(item)) {
    return 'ordered';
  } else if (checkExpired(item.mitrYukoKigenb)) {
    return 'expired';
  }
  return 'unexpired';
};

export const displayMultiLanguage = (
  productName,
  defaultName,
  data,
  isHakudoShhnm: boolean,
) => {
  // display product name in selected language
  if (!_.isEmpty(productName)) {
    return productName;
  }
  // english > japanese > first language of the list
  if (!_.isEmpty(isHakudoShhnm ? data?.hakudoShhnmEn : data?.shhnmEn)) {
    return isHakudoShhnm ? data?.hakudoShhnmEn : data?.shhnmEn;
  }
  if (!_.isEmpty(isHakudoShhnm ? data?.hakudoShhnmJp : data?.shhnmJp)) {
    return isHakudoShhnm ? data?.hakudoShhnmJp : data?.shhnmJp;
  }
  let listProductName: string[] = [];
  const listSortLanguage = sortLanguage();

  if (data) {
    for (const key in listSortLanguage) {
      switch (key) {
        case 'vi':
          listSortLanguage[key] = isHakudoShhnm
            ? data?.hakudoShhnmVn
            : data?.shhnmVn;
          break;
        case 'ko':
          listSortLanguage[key] = isHakudoShhnm
            ? data?.hakudoShhnmKr
            : data?.shhnmKr;
          break;
        case 'th':
          listSortLanguage[key] = isHakudoShhnm
            ? data?.hakudoShhnmTh
            : data?.shhnmTh;
          break;
        case 'zh':
          listSortLanguage[key] = isHakudoShhnm
            ? data?.hakudoShhnmCn
            : data?.shhnmCn;
          break;
        case 'tw':
          listSortLanguage[key] = isHakudoShhnm
            ? data?.hakudoShhnmTw
            : data?.shhnmTw;
          break;
        default:
          listSortLanguage[key] = '';
          break;
      }
      if (!_.isEmpty(listSortLanguage[key])) {
        listProductName.push(listSortLanguage[key]);
      }
    }
    if (listProductName.length > 0) {
      return _.get(listProductName, '0');
    }
    return defaultName;
  }
};

export const sortLanguage = () => {
  let siteInfo: any = LocalStorage.initSiteInfo;
  let listLanguage: any = [];
  const obj = {};
  siteInfo?.rmGengos?.map((obj) => {
    if (Object.values(obj)[0] !== 'en' && Object.values(obj)[0] !== 'ja') {
      listLanguage.push(Object.values(obj)[0]);
    }
  });
  listLanguage?.forEach((element) => {
    obj[element] = '';
  });
  return obj;
};

export const getProductName = (
  item,
  isHakudoShhnm: boolean,
  defaultName = '',
) => {
  let productName: string = '';
  switch (LocalStorage.lang) {
    case 'en':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmEn : item?.shhnmEn,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'ja':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmJp : item?.shhnmJp,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'vi':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmVn : item?.shhnmVn,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'ko':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmKr : item?.shhnmKr,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'th':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmTh : item?.shhnmTh,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'zh':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmCn : item?.shhnmCn,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    case 'tw':
      productName = displayMultiLanguage(
        isHakudoShhnm ? item?.hakudoShhnmTw : item?.shhnmTw,
        defaultName,
        item,
        isHakudoShhnm,
      );
      break;
    default:
      productName = defaultName;
      break;
  }
  return productName;
};

export const formatDimensions = (item, method: string) => {
  // Example: shiagsnpoHyojiMojirets = "15x15x1000(厚さｘ巾ｘ長さ)"
  //          inputSunpo = "1.5inch x 1.5ft x 100inch"
  //          snpoGuide = "厚さｘ巾ｘ長さ"
  //          shiagsnpo = "15x15x1000"
  //          sunpoTaniKubun = "mm"
  const labelSizes = item?.snpoGuide
    ? `${item?.sizes} (${item?.snpoGuide})`
    : `${item?.sizes || ''}`;

  if (item?.bunsuHandan) {
    if (item?.inputSunpo && item?.inputSunpo !== '') {
      return `${labelSizes} (${t(
        'pricing_page.estimate_results.reference_supplier_dimensions',
      )}${item?.shiagsnpoNyurk}${item?.sunpoTaniKubun})`;
    }
    return labelSizes;
  }

  if (item?.inputSunpo && item?.inputSunpo !== '' && item?.snpoGuide) {
    // If has convert size, display like this: 1.5inch x 1.5ft x 100inch (厚さｘ巾ｘ長さ) (参考 仕入先寸法15x15x1000mm)
    return `${item?.inputSunpo} (${item?.snpoGuide}) (${t(
      'pricing_page.estimate_results.reference_supplier_dimensions',
    )}${item?.shiagsnpoNyurk}${item?.sunpoTaniKubun})`;
  }
  if (item?.snpoGuide) {
    if (
      checkDimensionsWithSpaceBetween(item?.shiagsnpoNyurk) &&
      !isMachiningMethodFromH2ToH5(method)
    ) {
      return `${item?.shiagsnpoNyurk} (${item?.snpoGuide})`;
    }
    return formatSizeByMachiningMethod(item, method);
  }
  return labelSizes;
};

export const checkChangeLanguage = (prevLang, lang) => {
  if (prevLang !== lang) {
    return true;
  }
  return false;
};

export const formatManufacturingSizes = (text?: string) => {
  //example: text = 'Outer diameterxWall thickness(Dimension1xDimension2)'
  if (text) {
    //count 'x'
    const count = text?.split('x')?.length - 1;
    if (count > 1) {
      return text?.replaceAll('x', '\nx')?.replaceAll('(', ' \n(');
      //example: text = 'Outer diameter\nxWall thickness\n(Dimension1\nxDimension2)'
    }
    return text?.replaceAll('(', ' (');
  }
};

export const removeZeroAndDuplicateItem = (array?: string[]) => {
  if (array) {
    const newArray = array.filter((item) => item !== '0');
    return _.uniq(newArray).sort((a, b) => parseInt(a) - parseInt(b));
  } else return;
};

export const getUniqueSortedItemsByConvertedValue = (
  array?: {
    originalValue: string;
    convertedValue: string;
    processingRange?: Response.ProcessingRange | null;
  }[],
):
  | {
      originalValue: string;
      convertedValue: string;
      processingRange?: Response.ProcessingRange | null;
    }[]
  | [] => {
  if (!array) return [];

  // 1. Filter out elements with convertedValue === '0'
  const filtered = array.filter((item) => item.convertedValue !== '0');

  // 2. Use Map to remove duplicates based on convertedValue
  const uniqueMap = new Map<string, typeof filtered[0]>();

  for (const item of filtered) {
    if (!uniqueMap.has(item.convertedValue)) {
      uniqueMap.set(item.convertedValue, item);
    }
  }

  // 3. Create a result array from Map
  const uniqueArray = Array.from(uniqueMap.values());

  // 4. Sort by convertedValue in ascending numerical order
  const sortedArray = uniqueArray.sort(
    (a, b) => parseFloat(a.convertedValue) - parseFloat(b.convertedValue),
  );

  // 5. Return a new array containing only the processingRange if it exists.
  return sortedArray.map((item) => {
    const result: {
      originalValue: string;
      convertedValue: string;
      processingRange?: typeof item.processingRange;
    } = {
      originalValue: item.originalValue,
      convertedValue: item.convertedValue,
    };

    if (item.processingRange !== undefined) {
      result.processingRange = item.processingRange;
    }

    return result;
  });
};

export const removeDuplicateObject = (array) => {
  return array.filter((obj, index, arr) => {
    return index === arr.findIndex((o) => o.value === obj.value);
  });
};

export const getSizes = (
  data,
  isSizes1: boolean,
  roundedNumber: number = ROUND.TO_5_DIGIT,
) => {
  const newArray = data?.map((item) => {
    const getSizeValue = () => {
      //Ex: parseFloat(10.12345).toFixed(4) => '10.1235' => Number('10.1235').toString() => '10.1235'
      //    parseFloat(10).toFixed(4) => '10.0000' => Number('10.0000').toString() => '10'
      if (isSizes1) {
        return Number(
          parseFloat(getSizesById(item)).toFixed(roundedNumber),
        ).toString();
      }
      return Number(parseFloat(item).toFixed(roundedNumber)).toString();
    };
    const parts = getSizesById(item)?.split('x');
    if (_.size(parts) > 1) {
      //round to 4th decimal place
      const roundedParts = parts.map(
        (part) => +parseFloat(part).toFixed(roundedNumber),
      );
      return {
        label: roundedParts.join('x'),
        value: roundedParts.join('x'),
        key: item,
      };
    } else {
      return {
        label: getSizeValue(),
        value: getSizeValue(),
        key: item,
      };
    }
  });
  return newArray;
};

export const getSizesWithOriginalValue = (
  data: {
    originalValue: string;
    convertedValue: string;
    processingRange?: Response.ProcessingRange | null;
  }[],
  isSizes1: boolean,
  roundedNumber: number = ROUND.TO_5_DIGIT,
): {
  label: string;
  value: string;
  key: string;
  originalValue: string;
  processingRange?: Response.ProcessingRange | null;
}[] => {
  if (!data) return [];

  return data.map((item) => {
    const { originalValue, convertedValue, processingRange } = item;

    const getSizeValue = () => {
      if (isSizes1) {
        return Number(
          parseFloat(getSizesById(convertedValue)).toFixed(roundedNumber),
        ).toString();
      }
      return Number(
        parseFloat(convertedValue).toFixed(roundedNumber),
      ).toString();
    };

    const parts = getSizesById(convertedValue)?.split('x');

    if (_.size(parts) > 1) {
      const roundedParts = parts.map(
        (part) => +parseFloat(part).toFixed(roundedNumber),
      );
      const joined = roundedParts.join('x');
      return {
        label: joined,
        value: joined,
        key: convertedValue,
        originalValue,
        processingRange,
      };
    } else {
      const val = getSizeValue();
      return {
        label: val,
        value: val,
        key: convertedValue,
        originalValue,
        processingRange,
      };
    }
  });
};

function sortSizesByValueWithDelimiter(sizes: { value: string }[]) {
  const splitSizes = sizes.map(
    (item) => item?.value?.split('x').map((v) => parseFloat(v)) || [],
  );

  const sizesByIndex = sizes.map((size, idx) => {
    return { size, idx };
  });

  sizesByIndex.sort((it1, it2) => {
    const arr1 = splitSizes[it1.idx];
    const arr2 = splitSizes[it2.idx];

    for (let i = 0; i < Math.min(arr1.length, arr2.length); i++) {
      if (arr1[i] < arr2[i]) {
        return -1;
      } else if (arr1[i] > arr2[i]) {
        return 1;
      }
    }
    return arr1.length - arr2.length;
  });
  return sizesByIndex.map((each) => each.size);
}

export const sortSizesByValue = (sizes: { value: string }[]) => {
  // Some values in format: [{value: "10x20x30"}, {value: "10"}]
  const hasSizeValueWithDelimiter = _.some(sizes, (item) =>
    item.value.includes('x'),
  );

  if (hasSizeValueWithDelimiter) {
    return sortSizesByValueWithDelimiter(sizes);
  } else {
    return _.sortBy(sizes, (it) => parseFloat(it.value));
  }
};

export const getSizesById = (item) => {
  //item:  shhnCd-hakudoShhnCd-api1ShhnCd-api2ShhnCd-id
  //get code by id
  return (item = item?.split('-')[getIdOfSize(item)]);
};

export const getIdOfSize = (item) => {
  //item:  shhnCd-hakudoShhnCd-api1ShhnCd-api2ShhnCd-id
  return +item?.split('-')[4];
};

export const checkHasDomesticData = (data) => {
  //item:  shhnCd-hakudoShhnCd-api1ShhnCd-api2ShhnCd-id
  return data?.some((item) => getIdOfSize(item?.key) === 0);
};

export const isDomesticProduct = (product) => {
  // id of domestic product is "null-....."
  return product?.split('-')[1] !== 'null';
};

export const isImportedProduct = (product) => {
  // id of imported product is "....-null"
  return product?.split('-')[0] !== 'null';
};

export const isImportedProductBySize = (size) => {
  // Last segment (id) is not '0' for imported products
  return getIdOfSize(size) !== 0;
};

export const getDeliveryDateExternal = (text) => {
  //text example: 船便：30～45日以内\n航空機便：7~11日以内
  if (text) {
    const parts = text.split('\n');

    //Split each element into subvalues
    const byShipDate = parts[0]?.split('：')[1]?.split('日以内')[0];
    const byAirplaneDate = parts[1]?.split('：')[1]?.split('日以内')[0];

    const obj = {
      byShip: byShipDate,
      byAirplane: byAirplaneDate,
    };
    return obj;
  }
};

export const convertFinishedSize = (param: ConvertFinishedSizeType) => {
  // Example:
  // ThicknessxSide(Dimension1xDimension2)inchxfit     Length(Dimension3)fit
  //                0.0394x0.0492                   x          13.12            => 0.0394inch x 0.0492fit x 13.12fit
  //                0.0394x0.0492                   x                           => 0.0394inch x 0.0492fit x 1
  // ThicknessxSide(Dimension1xDimension2)inchxfit     Length(Dimension3)
  //                0.0394x0.0492                   x          13.12            => 0.0394inch x 0.0492fit x 13.12
  const { inputValue, manufacturingSizes, method, product, size, unitSize } =
    param;
  const sizeArray = size?.split('x');
  const unitSizeArray = unitSize?.split('x');
  const isImportedProductOnlyAndZ0 = isImportedProductOnly({
    product: product,
    checkZ0: true,
    method: method,
  });
  const isWidthOrLengthExist =
    (manufacturingSizes?.size1Label && inputValue?.product_width) ||
    (manufacturingSizes?.size2Label && inputValue?.product_length);

  const newArray = sizeArray.map((value, index) => {
    // if unitSize === 'empty' or unitSize === originSize => not show unitSize
    const shouldAppendUnitSizeArray =
      unitSizeArray[index] !== 'empty' &&
      unitSizeArray[index] !== manufacturingSizes?.originSize;

    if (isImportedProductOnlyAndZ0) {
      //if index < snpoSize
      const shouldAppendUnitSizeArrayForSnpo =
        index < manufacturingSizes?.snpoSize;
      // else if isWidthOrLengthExist
      const shouldAppendUnitSizeArrayForWidthOrLength =
        isWidthOrLengthExist && !shouldAppendUnitSizeArrayForSnpo;

      const shouldAppendUnitSizeArrayForImportedProduct =
        shouldAppendUnitSizeArray &&
        (shouldAppendUnitSizeArrayForSnpo ||
          shouldAppendUnitSizeArrayForWidthOrLength);

      return shouldAppendUnitSizeArrayForImportedProduct
        ? `${value}${unitSizeArray[index]}`
        : `${value}`;
    }

    return shouldAppendUnitSizeArray
      ? `${value}${unitSizeArray[index]}`
      : `${value}`;
  });

  if (checkAnyElementHasUnit(newArray)) {
    return newArray.join(' x ');
  }
  return newArray.join('x');
};

export const renderSize = (item, method: string) => {
  const labelSizes = item?.snpoGuide
    ? `${item?.sizes} (${item?.snpoGuide})`
    : `${item?.sizes}`;
  if (item?.bunsuHandan) {
    if (item?.inputSunpo && item?.inputSunpo !== '') {
      return `${labelSizes} (${t(
        'pricing_page.estimate_results.reference_supplier_dimensions',
      )}${item?.shiagsnpoNyurk}${item?.sunpoTaniKubun})`;
    }
    return labelSizes;
  }

  if (item?.inputSunpo && item?.inputSunpo !== '') {
    // Example: shiagsnpoHyojiMojirets = "15x15x1000(厚さｘ巾ｘ長さ)" (ImportedProduct : 15x15x1000 | DomesticProduct: 15mmx15mmx1000mm)
    // Example: inputSunpo = "1.5inchx1.5ftx100inch"

    // If has convert size, display like this: 1.5inchx1.5ftx100inch (厚さｘ巾ｘ長さ) (参考 仕入先寸法15x15x1000mm)
    return `${labelSizes} (${t(
      'pricing_page.estimate_results.reference_supplier_dimensions',
    )}${item?.shiagsnpoNyurk}${item?.sunpoTaniKubun})`;
  } else {
    // Else: 15x15x1000(厚さｘ巾ｘ長さ)
    return formatSizeByMachiningMethod(item, method);
  }
};

export const renderWeight = (henkanJury = '', jury = '') => {
  if (henkanJury && henkanJury !== '' && jury && jury !== '') {
    return `${henkanJury} (${jury})`;
  } else {
    return jury;
  }
};

export const isImportedProductOnly = (param: IsImportedProductOnlyParam) => {
  const { checkZ0, product, method } = param;
  const machiningMethodOfZ0AndH2ToH5 = [
    MACHINING_METHOD.Z0,
    ...MACHINING_METHOD.H2TOH5,
  ];
  if (checkZ0 && method) {
    return (
      isImportedProduct(product) &&
      !isDomesticProduct(product) &&
      machiningMethodOfZ0AndH2ToH5.includes(method)
    );
  }
  return isImportedProduct(product) && !isDomesticProduct(product);
};

export const convertDimensionsToArray = (dimensions) => {
  if (checkDimensionsWithSpaceBetween(dimensions)) {
    //example:13mm x 2000mm
    return dimensions?.split(' x ')?.map((item) => parseFloat(item).toString());
  }
  //example:13x2000
  return dimensions?.split('x')?.map((item) => parseFloat(item).toString());
};

export const convertShiagsnpoHyojiMojiretsToArray = (
  shiagsnpoHyojiMojirets,
) => {
  const dimensions = shiagsnpoHyojiMojirets?.split('(')[0]?.split('x');
  if (checkDimensionsWithSpaceBetween(shiagsnpoHyojiMojirets)) {
    // Example: shiagsnpoHyojiMojirets = "15mmx15mmx1000mm(厚さｘ巾ｘ長さ)" => [15mm,15mm,1000mm]
    return dimensions;
  }
  // Example: shiagsnpoHyojiMojirets = "15mmx15mmx1000mm(厚さｘ巾ｘ長さ)" => [15,15,1000]
  return dimensions?.map((item) => parseFloat(item).toString());
};

export const formatSizeByMachiningMethod = (item, method) => {
  // Split the snpo guide to get the length dimension position
  // Then round the length dimension to 2 decimal places
  // Example:
  // 1. shiagsnpoHyojiMojirets = "12x1333.333(直径x長さ)" => [12,1333.333]
  // Index of Length: 1, value: 1333.333, round to 2 decimal places -> 1333.33
  // 2. shiagsnpoHyojiMojirets = "12x15x15x1000x3.333(厚さx孔径xピッチx巾x長さ)" => [12x15x15,1000,3.333]
  // Index of Length: 2, value: 3.333, round to 2 decimal places -> 3.33

  // If product has maching method from H2 to H5 then split the dimensions and round the length.
  if (item?.shiagsnpoNyurk && isMachiningMethodFromH2ToH5(method)) {
    //If product has unit type (mm, cm, inch,...)
    const separator = checkDimensionsWithSpaceBetween(item?.shiagsnpoNyurk)
      ? ' x '
      : 'x';
    // item.shiagsnpoNyurk = 12mm x 1333.333mm
    return `${convertShiagsnpoHyojiMojiretsToArray(item.shiagsnpoNyurk)
      // arr = [12mm, 1333.333mm]
      ?.map((dimension, index, arr) =>
        // Get last element = 1333.333mm
        arr.length > 1 && index === arr.length - 1
          ? // Round to 2 decimal places
            // arr[1] = 1333.33
            roundDecimalPlaces(parseFloat(dimension), 2) +
            // unit = mm
            dimension.replace(REGEX.REGEX_NUMERIC_CHARACTER, '').trim()
          : dimension,
      )
      // '12mm' + ' x ' + '1333.33' + 'mm' + ' ' + '(直径x長さ)'
      .join(separator)}${separator === ' x ' ? ' ' : ''}(${item.snpoGuide})`;
  }
  // If not, return original snpo guide
  return `${item?.shiagsnpoNyurk}\n(${item?.snpoGuide})`;
};

export const renderNoukiDisplay = (noukiDisplay) => {
  if (checkNoukiDisplay(noukiDisplay)) {
    return t('pricing_page.estimate_results.delivery_date_external', {
      0: getDeliveryDateExternal(noukiDisplay)?.byShip,
      1: getDeliveryDateExternal(noukiDisplay)?.byAirplane,
    });
  } else {
    return noukiDisplay;
  }
};

export const checkDimensionsWithSpaceBetween = (dimensions) => {
  return dimensions?.split(' x ')?.length > 1;
};

export const checkAnyElementHasUnit = (arr) => {
  const hasUnit = arr.some((element) => {
    const unit = element.match(REGEX.REGEX_ALPHABETIC_CHARACTERS); // Search for unit in the element
    if (unit !== null && unit.length > 0) {
      // If a unit is found
      return true;
    } else {
      return false;
    }
  });
  return hasUnit;
};

export const encryptData = (data: string) => {
  if (process.env.REACT_APP_PUBLIC_KEY) {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(process.env.REACT_APP_PUBLIC_KEY);
    return encrypt.encrypt(data) || '';
  }
  return '';
};

export const isMachiningMethodFromH2ToH5 = (method: string) => {
  return MACHINING_METHOD.H2TOH5.includes(method);
};

export const roundDecimalPlaces = (size, decimalPlaces: number) => {
  return Number(parseFloat(size).toFixed(decimalPlaces));
};

export const roundSizeForMachiningMethodFromH2ToH5 = (
  size: string | undefined,
  method: string,
) => {
  return size && isMachiningMethodFromH2ToH5(method)
    ? Number(parseFloat(size).toFixed(2)).toString()
    : size;
};

export const formatSizeToFiveDecimalsIfMethodIsH2ToH5 = (
  size: string | undefined,
  method: string,
) => {
  return size && isMachiningMethodFromH2ToH5(method)
    ? Number(parseFloat(size).toFixed(5)).toString()
    : size;
};

export const getColumnNameMulti = (item, defaultName, colKey) => {
  let columnName: string = '';
  const colNumber = Number(colKey?.substring(1));
  switch (LocalStorage.lang) {
    case 'en':
      columnName = _.isEmpty(item?.colmEn)
        ? `${defaultName + colNumber}`
        : item?.colmEn;
      break;
    case 'ja':
      columnName = _.isEmpty(item?.colmJp)
        ? `${defaultName + colNumber}`
        : item?.colmJp;
      break;
    case 'vi':
      columnName = _.isEmpty(item?.colmVn)
        ? `${defaultName + colNumber}`
        : item?.colmVn;
      break;
    case 'ko':
      columnName = _.isEmpty(item?.colmKr)
        ? `${defaultName + colNumber}`
        : item?.colmKr;
      break;
    case 'th':
      columnName = _.isEmpty(item?.colmTh)
        ? `${defaultName + colNumber}`
        : item?.colmTh;
      break;
    case 'zh':
      columnName = _.isEmpty(item?.colmCn)
        ? `${defaultName + colNumber}`
        : item?.colmCn;
      break;
    case 'tw':
      columnName = _.isEmpty(item?.colmTw)
        ? `${defaultName + colNumber}`
        : item?.colmTw;
      break;
    default:
      columnName = `${defaultName + colNumber}`;
      break;
  }
  return columnName;
};

export const createFileFromString = (data: string, fileName: string) => {
  const downloadUrl = URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const checkAllPropertiesNull = (obj) => {
  return Object.values(obj).every((value) => value === null);
};

export const checkIsProductFinished = (data) => {
  return data === CATEGORIES_ID.TOOL_PRODUCTS;
};

export const isAllTrue = (obj) => {
  return Object.values(obj).every((value) => value === true);
};

export const handleRedirectProductDetail = (product, hinshL3Cd, pathname) => {
  const queryParams: { [key: string]: string } = {};
  const propertiesToCheck = [
    'shhnCd',
    'hakudoShhnCd',
    'api1ShhnCd',
    'api2ShhnCd',
  ];

  propertiesToCheck.forEach((property) => {
    if (product?.[property]) {
      queryParams[property] = product[property];
    }
  });
  queryParams.hinshL3Cd = hinshL3Cd;
  history.push({
    pathname: pathname,
    search: qs.stringify(queryParams),
  });
};

export const findOriginSizeByApiList = (
  arrayData,
  value,
  priceApiList,
  listProductId,
  product,
  preferredApiSeqno?: number,
): string => {
  const sizeKey =
    arrayData?.find((item) => item.value === value)?.key ??
    product
      .split('-')
      .map((x) => (x === 'null' ? x : value))
      .join('-') + `-${value}`;

  const productIdMap: Record<number, string> = {
    1: 'hakudoShhnCd',
    2: 'api1ShhnCd',
    3: 'api2ShhnCd',
  };

  // If preferredApiSeqno is provided, use it directly
  if (preferredApiSeqno !== undefined) {
    const parts = sizeKey.split('-');
    parts[preferredApiSeqno] = value || 'null';
    parts[parts.length - 1] = preferredApiSeqno;
    return parts.join('-');
  }

  // Original logic: find first matched API
  const matchedApi = priceApiList?.find((api) => {
    const key = productIdMap[api.seqno];
    return key && listProductId[key];
  });

  if (!matchedApi) return sizeKey;

  const parts = sizeKey.split('-');
  parts[matchedApi.seqno] = value || 'null';
  // If it's a domestic product, the last value should be 0.
  parts[parts.length - 1] = isDomesticProduct(product) ? '0' : matchedApi.seqno;

  return parts.join('-');
};

export const findOriginSize = (arrayData, value) => {
  const sizesData = arrayData?.find((item) => item.value === value)?.key;
  return sizesData;
};

export const checkNoukiDisplay = (noukiDisplay) => {
  //noukiDisplay: "船便：30～45日以内\n航空機便：7~11日以内" OR "2024-04-25 10:53:37"
  return noukiDisplay?.includes('日以内');
};

export const checkBackOrderProductAndNoukiDisplay = (item) => {
  return (
    item?.zaitokKbn === ProductType.BACK_ORDER_PRODUCT &&
    checkNoukiDisplay(item?.noukiDisplay)
  );
};

/**
 * Assigns fraction-based displays for value/label, supports 'x'-separated multiparts.
 * @param {string} src
 * @param {Array} fractionsData
 * @param {boolean} useLabel
 * @returns {string}
 */
export const mapPartsWithFractions = (
  src,
  itemOriginalValue,
  fractionsData,
  snpoBunsuHyojis,
) => {
  if (!src || !itemOriginalValue) return src;

  const srcParts = src.split('x');
  const originalParts = itemOriginalValue.split('x');
  const bunKey = (idx: number) => `snpo${idx}BunsuHyoji`;
  const get = (idx: number) => snpoBunsuHyojis[bunKey(idx)];
  return originalParts
    .map((originalPart, index) => {
      const matched = fractionsData?.find(
        (fd) =>
          Number(
            parseFloat(fd.decimalDisplay).toFixed(ROUND.TO_5_DIGIT),
          ).toString() === originalPart,
      );

      if (matched) {
        return get(index + 1) === '1'
          ? matched.fractionDisplay
          : matched.decimalDisplay;
      }
      return srcParts[index] ?? originalPart;
    })
    .join('x');
};

/**
 * Maps array of options using fraction logic for their .value and .label fields.
 * For "x"-separated value/label, applies per part. Fallback: original value/label.
 * @param {Array} dataArray - array of objects with 'value' and optional 'label'
 * @param {Array} fractionsData
 * @returns {Array}
 */
/**
 * Maps array of options using fraction logic for their .value and .label fields.
 * For "x"-separated value/label, applies per part. Fallback: original value/label.
 * @param {Array} dataArray - array of objects with 'value' and optional 'label'
 * @param {Array} fractionsData
 * @param {boolean} isDomesticProductFlag
 * @param {string} machiningMethod
 * @returns {Array}
 */
export const mapDataOptionsWithFractions = (
  dataArray,
  fractionsData,
  isDomesticProductFlag?,
  machiningMethod?,
  isRoundedTo2Digits?,
  snpoBunsuHyojis?,
) => {
  const isMachiningMethodOfZ0AndH2ToH5 = [
    MACHINING_METHOD.Z0,
    ...MACHINING_METHOD.H2TOH5,
  ].includes(machiningMethod);
  const isMachiningMethodH2ToH5 =
    MACHINING_METHOD.H2TOH5.includes(machiningMethod);

  // Helper to find matched fraction for a value
  const findMatchedFraction = (value) => {
    if (!fractionsData) return undefined;

    // Determine which decimal places to match based on flags
    const decimalStr = (s, n) => Number(parseFloat(s).toFixed(n)).toString();

    return fractionsData.find((fd) => {
      if (isDomesticProductFlag && isMachiningMethodOfZ0AndH2ToH5) {
        if (_.size(dataArray) > 1) {
          return (
            decimalStr(
              fd.decimalDisplay,
              isRoundedTo2Digits ? ROUND.TO_2_DIGIT : ROUND.TO_5_DIGIT,
            ) === value
          );
        }
        if (isMachiningMethodH2ToH5) {
          return decimalStr(fd.decimalDisplay, ROUND.TO_2_DIGIT) === value;
        }
        return fd.decimalDisplay === value;
      }
      // Default fallback to 5 digits
      return decimalStr(fd.decimalDisplay, ROUND.TO_5_DIGIT) === value;
    });
  };

  if (!dataArray) return [];

  return dataArray.map((item) => {
    const itemOriginalValue = `${item.originalValue ?? ''}`;
    const itemValue = `${item.value ?? ''}`;
    const itemLabel = `${item.label ?? itemValue}`;

    // Handle "x"-separated multi-part
    if (itemValue.includes('x')) {
      return {
        ...item,
        label: mapPartsWithFractions(
          itemValue,
          itemOriginalValue,
          fractionsData,
          snpoBunsuHyojis,
        ),
      };
    }

    // Single-part handling
    const matchedFraction = findMatchedFraction(itemOriginalValue);
    let valueResult = itemValue;
    if (matchedFraction) {
      if (isDomesticProductFlag && isMachiningMethodOfZ0AndH2ToH5) {
        // Domestic and Z0/H2-H5
        valueResult = isMachiningMethodH2ToH5
          ? Number(parseFloat(itemValue).toFixed(ROUND.TO_2_DIGIT)).toString()
          : itemValue;
      } else {
        // Non-domestic or non-special method
        valueResult = Number(
          parseFloat(itemValue).toFixed(ROUND.TO_5_DIGIT),
        ).toString();
      }
    }
    return {
      ...item,
      value: valueResult,
      label: matchedFraction ? matchedFraction.fractionDisplay : itemLabel,
    };
  });
};

export const getSnpoBunsuHyojiFlags = (
  snpoSizesnpoSize: Record<string, string | null> | null,
  snpoSize: number,
): Payload.DecimalInputSizeFlags => {
  if (!snpoSizesnpoSize)
    return {
      isSnpo1BunsuHyoji: false,
      isSnpo2BunsuHyoji: false,
      isSnpo3BunsuHyoji: false,
      isSnpo4BunsuHyoji: false,
      isSnpo5BunsuHyoji: false,
      isSnpo6BunsuHyoji: false,
    };
  // Helper to get value at index, returns undefined if not found
  const bunKey = (idx: number) => `snpo${idx}BunsuHyoji`;
  const get = (idx: number) => snpoSizesnpoSize[bunKey(idx)];

  // 1. isSnpo1BunsuHyoji: check from index 1 to snpoSize, any value === "1"
  let isSnpo1BunsuHyoji = false;
  for (let i = 1; i <= snpoSize; i++) {
    if (get(i) === '1') {
      isSnpo1BunsuHyoji = true;
      break;
    }
  }

  // 2. isSnpo2BunsuHyoji: check at snpoSize
  const isSnpo2BunsuHyoji = get(snpoSize + 1) === '1';

  // 3. isSnpo3BunsuHyoji: check at snpoSize+1; if not exists OR === "1" => true
  const val3 = get(snpoSize + 2);
  const isSnpo3BunsuHyoji = val3 === '1';

  // 4. isSnpo4BunsuHyoji: snpoSize+3; if not exists OR === "1"
  const val4 = get(snpoSize + 3);
  const isSnpo4BunsuHyoji = val4 === '1';

  // 5. isSnpo5BunsuHyoji: snpoSize+4; if not exists OR === "1"
  const val5 = get(snpoSize + 4);
  const isSnpo5BunsuHyoji = val5 === '1';

  // 6. isSnpo6BunsuHyoji: snpoSize+5; if not exists OR === "1"
  const val6 = get(snpoSize + 5);
  const isSnpo6BunsuHyoji = val6 === '1';

  return {
    isSnpo1BunsuHyoji,
    isSnpo2BunsuHyoji,
    isSnpo3BunsuHyoji,
    isSnpo4BunsuHyoji,
    isSnpo5BunsuHyoji,
    isSnpo6BunsuHyoji,
  };
};

/**
 * Add snpo1Label to each item in detailInfoData array based on fraction and bunsu display logic.
 * @param fractionsData Array of { fractionDisplay, decimalDisplay }
 * @param detailInfoDataRaw Array of detail info objects (each with snpo1)
 * @param snpoBunsuHyojisInfoData Object with snpo<n>BunsuHyoji property ('1' means display with fraction if exists)
 */
export const addSnpo1LabelToDetailInfo = (
  fractionsData: Response.Fractions[],
  detailInfoDataRaw: Response.ProductDetailInfoItem[] = [],
  snpoBunsuHyojisInfoData: Response.SnpoBunsuHyojisItem | {} = {},
): Response.ProductDetailInfoItem[] => {
  if (!Array.isArray(detailInfoDataRaw)) return [];

  return detailInfoDataRaw.map((item) => {
    const snpo1 = item.snpo1;
    // Protect against non-string snpo1 (possible?)
    if (typeof snpo1 !== 'string') return { ...item, snpo1Label: snpo1 };

    const parts = snpo1.split('x');
    let result: string[] = [];

    // Up to 6 bunsu display toggles, propert names: snpo1BunsuHyoji ... snpo6BunsuHyoji
    for (let i = 0; i < parts.length && i < 6; i++) {
      const key = `snpo${i + 1}BunsuHyoji`;
      const bunsuHyoji = snpoBunsuHyojisInfoData?.[key];

      if (bunsuHyoji === '1') {
        // Compare part with all decimalDisplay in fractionsData
        const found = fractionsData?.find(
          (f) => String(f.decimalDisplay) === String(parts[i]),
        );
        result.push(found ? found.fractionDisplay : parts[i]);
      } else {
        result.push(parts[i]);
      }
    }

    // Any further segments that exceed bunsu toggles just append raw value
    if (parts.length > 6) {
      result = [...result, ...parts.slice(6)];
    }

    // If snpo1 is not split (no 'x'), result is length 1
    // For spec: if bunsuHyoji !== '1', snpo1Label is snpo1
    // Already handled above

    return {
      ...item,
      snpo1Label: result.join('x'),
    };
  });
};

/**
 * Processes manufacturing size data with original logic + preserves processingRange.
 * @param {Array<{ sizeS1: string, processingRange?: any }>} items
 * @param {boolean} [isSizes1=true] Whether to process using getSizes logic for sizes1.
 * @param {number} [roundedNumber=4] How many decimal digits to round size values.
 * @returns {Array<{ label: string, value: string, key: string, processingRange?: any }>}
 */
export function getManufacturingSizeObjectsWithProcessingRange(
  sizes,
  processingRange,
) {
  if (!Array.isArray(sizes)) return [];
  // Only use sizeS1, remove '0' and duplicates, sort ascending (like original logic does for sizes array)

  // Map by index (after cleaning), and merge in processingRange
  return sizes.map((obj) => {
    // Find processingRange using key matching original entry
    const matchingItem = processingRange?.find(
      (item) => item.sizeS1 === obj.key,
    );
    return {
      ...obj,
      processingRange: matchingItem?.processingRange ?? null,
    };
  });
}

// ================================
// getShowProcessingRange
// ================================
/**
 * Common utility: getShowProcessingRange
 * @param {object} paramValues - { valueProductThickness, valueProductWidth, valueProductLength, valueDimension4, valueDimension5, processingRange, machiningMethod }
 * @returns {getShowProcessingRange}
 */
export function getShowProcessingRange({
  valueProductThickness,
  valueProductWidth,
  valueProductLength,
  valueDimension4,
  valueDimension5,
  processingRanges,
  machiningMethod,
}: Payload.ParamsShowProcessingRange): string[] {
  if (
    !valueProductThickness &&
    !valueProductWidth &&
    !valueProductLength &&
    !valueDimension4 &&
    !valueDimension5
  ) {
    return [];
  }
  // Defensive
  if (!processingRanges || !Array.isArray(processingRanges) || !machiningMethod)
    return [];
  // Helper: Locate in processingRangeBySize (array) by "value" or more complex structure
  const matchItem = processingRanges.find(
    (item) =>
      item.value === valueProductThickness ||
      item.label === valueProductThickness,
  );

  // Helper: Parse range text like "20~300" or "4~30"
  const findRange = (rangesFirst, rangesSecond, value): string => {
    if (!Array.isArray(rangesFirst) || !Array.isArray(rangesSecond) || !value)
      return '';
    const numericValue = parseFloat(value);

    for (let i = 0; i < rangesFirst.length; i++) {
      const [minStr, maxStr] = rangesFirst[i].split('~');
      const min = parseFloat(minStr);
      const max = parseFloat(maxStr);

      if (numericValue >= min && numericValue <= max) {
        return rangesSecond[i] || '';
      }
    }
    return '';
  };
  // Helper: Combine min/max from string ranges
  const combineRange = (ranges) => {
    if (!ranges || !Array.isArray(ranges) || ranges.length === 0) return '';
    let min = Infinity,
      max = -Infinity;
    for (let rg of ranges) {
      if (!rg) continue;
      const [a, b] = rg.split('~').map(Number);
      if (!isNaN(a)) min = Math.min(min, a);
      if (!isNaN(b)) max = Math.max(max, b);
    }
    return min !== Infinity && max !== -Infinity ? `${min}~${max}` : '';
  };

  // CASE 1: Not in LIMITS
  if (!MACHINING_PROCESSING_RANGES.includes(machiningMethod)) {
    if (!matchItem) return [];
    // For valueProductThickness as single or compound (e.g. "10x100x100"), handle splitting
    let valueCatx = valueProductThickness
      ? valueProductThickness.split('x')
      : [];
    if (valueCatx.length > 1) {
      const pr = matchItem.processingRange || {};
      const ranges = [
        (pr[`processingRange${valueCatx.length + 0}`] &&
          pr[`processingRange${valueCatx.length + 0}`][0]) ||
          '',
        (pr[`processingRange${valueCatx.length + 1}`] &&
          pr[`processingRange${valueCatx.length + 1}`][0]) ||
          '',
        (pr[`processingRange${valueCatx.length + 2}`] &&
          pr[`processingRange${valueCatx.length + 2}`][0]) ||
          '',
        (pr[`processingRange${valueCatx.length + 3}`] &&
          pr[`processingRange${valueCatx.length + 3}`][0]) ||
          '',
        (pr[`processingRange${valueCatx.length + 4}`] &&
          pr[`processingRange${valueCatx.length + 4}`][0]) ||
          '',
      ].filter((v) => v !== '');
      return ranges;
    } else {
      const pr = matchItem.processingRange || {};
      const ranges = [
        (pr.processingRange1 && pr.processingRange1[0]) || '',
        (pr.processingRange2 && pr.processingRange2[0]) || '',
        (pr.processingRange3 && pr.processingRange3[0]) || '',
        (pr.processingRange4 && pr.processingRange4[0]) || '',
        (pr.processingRange5 && pr.processingRange5[0]) || '',
        (pr.processingRange6 && pr.processingRange6[0]) || '',
      ].filter((v) => v !== '');
      return ranges;
    }
  }
  // CASE 2: In LIMITS
  if (MACHINING_PROCESSING_RANGES.includes(machiningMethod)) {
    if (!matchItem) return [];
    let valueCatx = valueProductThickness
      ? valueProductThickness.split('x')
      : [];
    let pr = matchItem.processingRange || {};
    // For compound thickness
    if (valueCatx.length > 1) {
      // productWidthProcessingRange is combined range of the corresponding array
      const thicknessRanges = pr[`processingRange${valueCatx.length - 1}`];
      const widthRanges = pr[`processingRange${valueCatx.length}`];
      const lengthRanges = pr[`processingRange${valueCatx.length + 1}`];
      const dim4Ranges = pr[`processingRange${valueCatx.length + 2}`];
      const dim5Ranges = pr[`processingRange${valueCatx.length + 3}`];
      const dim6Ranges = pr[`processingRange${valueCatx.length + 4}`];
      const ranges = [
        combineRange(thicknessRanges),
        combineRange(widthRanges),
        findRange(widthRanges, lengthRanges, valueProductWidth),
        findRange(lengthRanges, dim4Ranges, valueProductLength),
        findRange(dim4Ranges, dim5Ranges, valueDimension4),
        findRange(dim5Ranges, dim6Ranges, valueDimension5),
      ].filter((v) => v !== '');
      return ranges;
    } else {
      // productWidthProcessingRange combines min/max, rest check value within the array
      const ranges = [
        combineRange(pr.processingRange1),
        combineRange(pr.processingRange2),
        findRange(pr.processingRange2, pr.processingRange3, valueProductWidth),
        findRange(pr.processingRange3, pr.processingRange4, valueProductLength),
        findRange(pr.processingRange4, pr.processingRange5, valueDimension4),
        findRange(pr.processingRange5, pr.processingRange6, valueDimension5),
      ].filter((v) => v !== '');
      return ranges;
    }
  }
  return [];
}

export function normalizeParenthesis(productName) {
  if (!productName) return '';

  const lastIndex = productName.lastIndexOf('）');
  if (lastIndex === -1) return productName;
  return (
    productName.slice(0, lastIndex) + ')' + productName.slice(lastIndex + 1)
  );
}
